import { FC, ReactNode } from 'react'

import { Box, Skeleton } from '@mui/material'

interface DialogRefetchOverlayProps {
  /**
   * When `true`, the wrapped content is dimmed and a Skeleton loading
   * indicator is overlaid on top to signal a dialog-level refresh (e.g. after
   * the network filter changes). The content keeps its layout/size so the
   * dialog paper does not visibly resize during the refresh.
   */
  active: boolean
  children: ReactNode
}

/**
 * Dialog-level refresh overlay. Wrap the fully-rendered dialog body in this
 * so that when data is being re-fetched in place (e.g. network switch), the
 * user sees one coherent "dialog is refreshing" signal — dimmed previous
 * content with a Skeleton loading pill — instead of partial flickers as
 * individual bits of the content update asynchronously. We use a Skeleton
 * (rather than a spinner) to stay consistent with the loading affordance used
 * everywhere else in the feature.
 *
 * While active, the content is made non-interactive and hidden from assistive
 * tech to match the "this view is being replaced" semantic.
 */
export const DialogRefetchOverlay: FC<DialogRefetchOverlayProps> = ({ active, children }) => (
  <Box sx={{ position: 'relative' }}>
    <Box
      aria-busy={active}
      aria-hidden={active}
      sx={{
        opacity: active ? 0.35 : 1,
        transition: 'opacity 150ms ease-out',
        pointerEvents: active ? 'none' : 'auto',
      }}
    >
      {children}
    </Box>
    {active && (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Skeleton variant="rounded" animation="wave" width={220} height={40} sx={{ borderRadius: 999 }} />
      </Box>
    )}
  </Box>
)
